/**
 * Contract Pack status surfaces.
 *
 * Exports:
 *  - <ContractStatusCard /> — full card with three milestone rows, shown
 *    in the customer file's right column. Each pending pill is now
 *    INTERACTIVE — clicking it jumps to the relevant action.
 *  - <ContractStatusPill /> — compact one-line summary badge intended for
 *    the customer-file header (lives next to the stage + payment + DNO pills).
 *
 * Both components hit the same `GET /api/customers/{id}/contract-pack`
 * endpoint and pick the latest non-draft pack (falling back to the latest
 * draft).
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import api from '../lib/api';
import { FileSignature, CheckCircle2, Send, Loader2, Circle, Edit3, Eye } from 'lucide-react';
import { toast } from 'sonner';

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return ''; }
}

function formatDateTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return ''; }
}

// Shared fetch — keeps both components honest about which pack they care
// about. Returns null when there's no pack, the pack dict otherwise.
async function fetchLatestPack(customerId) {
  try {
    const { data } = await api.get(`/customers/${customerId}/contract-pack`);
    const nonVoid = (data || []).filter((p) => p.status !== 'voided');
    const live = nonVoid.find((p) => p.status !== 'draft') || nonVoid[0] || null;
    return live;
  } catch {
    return null;
  }
}

const PillRow = ({
  label, achieved, date, icon: Icon, color, onClick, actionHint,
}) => {
  const clickable = !achieved && !!onClick;
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onClick}
      className={`w-full flex items-start gap-2.5 py-1.5 px-2 -mx-2 rounded-md text-left transition-colors
        ${clickable
          ? 'cursor-pointer hover:bg-blue-50/70 focus:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200'
          : achieved ? 'cursor-default' : 'cursor-default opacity-95'
        }`}
      data-testid={`contract-pill-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0 ${achieved ? color.bg : 'bg-slate-100'}`}>
        {achieved
          ? <CheckCircle2 className={`w-4 h-4 ${color.text}`} />
          : <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </span>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${achieved ? 'text-slate-900' : 'text-slate-400'}`}>
          {label}
        </p>
        {achieved && date && (
          <p className="text-xs text-slate-500 mt-0.5">{formatDate(date)}</p>
        )}
        {!achieved && (
          <p className={`text-xs mt-0.5 ${clickable ? 'text-blue-600' : 'text-slate-300'}`}>
            {clickable ? actionHint : 'Pending'}
          </p>
        )}
      </div>
    </button>
  );
};

export const ContractStatusCard = ({
  customerId,
  onOpenWizard,        // () => void — open ContractPackWizard
  onScrollToPacks,     // () => void — scroll user to ContractPacksSection
}) => {
  const [loading, setLoading] = useState(true);
  const [pack, setPack] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const live = await fetchLatestPack(customerId);
      if (alive) {
        setPack(live);
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [customerId]);

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="contract-status-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-blue-600" />
            Contract Pack
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        </CardContent>
      </Card>
    );
  }

  const status = pack?.status || null;
  const sent = !!pack?.sent_at;
  const viewed = !!pack?.customer_first_viewed_at;
  const customerSigned = status === 'customer_signed' || status === 'fully_signed' || !!pack?.customer_signed_at;
  const complete = status === 'fully_signed' || !!pack?.admin_signed_at;

  // --- Click actions per pill ---
  const handleSentClick = () => onOpenWizard?.();

  const handleCustomerSignedClick = async () => {
    // Copy the public signing URL so the admin can re-share it with the
    // customer (slow signers / lost emails / re-prompts).
    const token = pack?.sign_token;
    if (!token) {
      toast.error('No active signing link — open the wizard and click "Send for signing" first.');
      return;
    }
    const url = `${window.location.origin}/sign-contract/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Signing link copied to clipboard — paste it into a message to the customer.');
    } catch {
      // Older browsers / non-HTTPS fallback
      window.prompt('Copy this signing link:', url);
    }
  };

  const handleCompleteClick = () => {
    // The existing ContractPacksSection has the "Sign Now (Helios)" button
    // and counter-sign dialog — scrolling there is more discoverable than
    // re-implementing the dialog here.
    onScrollToPacks?.();
    if (!onScrollToPacks) {
      toast.info('Scroll down to the Contract Packs section to counter-sign.');
    }
  };

  return (
    <Card className="border-slate-200" data-testid="contract-status-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-blue-600" />
            Contract Pack
          </span>
          {pack && (
            <span
              className={`text-xs font-normal px-2 py-0.5 rounded-full border ${
                complete ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                customerSigned ? 'bg-violet-50 border-violet-200 text-violet-700' :
                sent ? 'bg-blue-50 border-blue-200 text-blue-700' :
                'bg-slate-50 border-slate-200 text-slate-500'
              }`}
              data-testid="contract-status-badge"
            >
              {complete ? 'Complete' :
               customerSigned ? 'Customer signed' :
               sent ? 'Sent for signing' :
               'Draft'}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {pack ? (
          <div className="space-y-0.5">
            <PillRow
              label="Sent for signing"
              achieved={sent || customerSigned || complete}
              date={pack.sent_at}
              icon={Send}
              color={{ bg: 'bg-blue-100', text: 'text-blue-700' }}
              onClick={handleSentClick}
              actionHint="Click to open the Contract Pack Wizard"
            />
            <PillRow
              label="Viewed by customer"
              achieved={viewed || customerSigned || complete}
              date={pack.customer_last_viewed_at || pack.customer_first_viewed_at}
              icon={Eye}
              color={{ bg: 'bg-sky-100', text: 'text-sky-700' }}
              onClick={null}
              actionHint={sent ? 'Waiting for customer to open the link' : 'Send the pack first'}
            />
            {viewed && (pack.customer_view_count || 0) > 1 && (
              <p className="text-[11px] text-slate-500 -mt-1 mb-1 pl-9">
                Opened {pack.customer_view_count} times · last on {formatDateTime(pack.customer_last_viewed_at)}
              </p>
            )}
            <PillRow
              label={pack?.signed_externally ? 'Customer signed (DocHub)' : 'Customer signed'}
              achieved={customerSigned || complete}
              date={pack.customer_signed_at}
              icon={Circle}
              color={{ bg: 'bg-violet-100', text: 'text-violet-700' }}
              onClick={sent ? handleCustomerSignedClick : handleSentClick}
              actionHint={sent ? 'Click to copy the signing link' : 'Send the pack first'}
            />
            <PillRow
              label="Complete"
              achieved={complete}
              date={pack.admin_signed_at}
              icon={Circle}
              color={{ bg: 'bg-emerald-100', text: 'text-emerald-700' }}
              onClick={customerSigned ? handleCompleteClick : (sent ? handleCustomerSignedClick : handleSentClick)}
              actionHint={customerSigned
                ? 'Click to counter-sign for Helios'
                : sent ? 'Waiting on customer signature'
                : 'Send the pack first'}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={onOpenWizard}
            className="w-full text-left text-sm text-slate-600 italic hover:text-blue-600 hover:bg-blue-50/60 rounded-md px-2 py-2 -mx-2 transition-colors"
            data-testid="no-contract-cta"
          >
            <Edit3 className="w-4 h-4 inline mr-1.5 align-text-bottom" />
            No contract pack yet — click here to open the Contract Pack Wizard.
          </button>
        )}
      </CardContent>
    </Card>
  );
};

// --- Compact pill for the page header ----------------------------------
export const ContractStatusPill = ({ customerId, onClick }) => {
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const live = await fetchLatestPack(customerId);
      if (alive) {
        setPack(live);
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [customerId]);

  if (loading) return null;

  const status = pack?.status || null;
  const sent = !!pack?.sent_at;
  const customerSigned = status === 'customer_signed' || status === 'fully_signed' || !!pack?.customer_signed_at;
  const complete = status === 'fully_signed' || !!pack?.admin_signed_at;

  const tone =
    !pack ? { cls: 'bg-slate-50 border-slate-300 text-slate-600', label: 'Contract: Not started' } :
    complete ? { cls: 'bg-emerald-50 border-emerald-300 text-emerald-700', label: 'Contract: Complete' } :
    customerSigned ? { cls: 'bg-violet-50 border-violet-300 text-violet-700', label: 'Contract: Customer signed' } :
    sent ? { cls: 'bg-blue-50 border-blue-300 text-blue-700', label: 'Contract: Awaiting signature' } :
    { cls: 'bg-amber-50 border-amber-300 text-amber-700', label: 'Contract: Draft' };

  return (
    <button
      type="button"
      onClick={onClick}
      title="Jump to Contract Pack section"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors hover:opacity-90 ${tone.cls}`}
      data-testid="contract-status-header-pill"
    >
      <FileSignature className="w-3 h-3" />
      {tone.label}
    </button>
  );
};

export default ContractStatusCard;
